import React, { useEffect, useMemo, useRef, useState } from "react";
import { Order, OrderStatus } from "../types";
import { orderService } from "../services/dataService";
import { formatCurrency, getImageUrl } from "../utils/formatters";
import AlertModal from "./AlertModal";
import "../pages/PurchaseOrders.css";
import "./OrderDetailModal.css";

interface OrderDetailModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
}

interface OrderLog {
  logId: string;
  orderId: string;
  changedAt: string;
  changeType: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  changedByUserName: string;
  description?: string;
  changeDescription?: string;
  changeToken?: string;
}

interface GroupedChange {
  changeToken: string;
  changedAt: string;
  changedByUserName: string;
  changes: OrderLog[];
}

type AlertState = { isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" };

/* ---------- sabitler / yardımcılar ---------- */
const STATUS: Record<number, { label: string; tone: string }> = {
  [OrderStatus.Atanmadi]: { label: "Atanmadı", tone: "warn" },
  [OrderStatus.Islemde]: { label: "İşlemde", tone: "info" },
  [OrderStatus.Tamamlandi]: { label: "Tamamlandı", tone: "ok" },
  [OrderStatus.IptalEdildi]: { label: "İptal edildi", tone: "err" },
};
const UNIT_LABEL: Record<number, string> = { 1: "Adet", 2: "Metre", 3: "Takım", 7: "Pano" };
const FIELD_LABEL: Record<string, string> = {
  Status: "Durum", StatusChanged: "Durum", Workshop: "Atölye", WorkshopChanged: "Atölye",
  Operator: "Sorumlu", OperatorChanged: "Sorumlu", AssignedUser: "Sorumlu",
  Price: "Fiyat", PriceChanged: "Fiyat", PriceCurrency: "Para birimi",
  Quantity: "Miktar", QuantityChanged: "Miktar", Priority: "Öncelik", PriorityChanged: "Öncelik",
  Deadline: "Termin", DeadlineChanged: "Termin", Note: "Not", NoteChanged: "Not",
  Model: "Model", ModelChanged: "Model", Firm: "Firma", FirmChanged: "Firma", Modelist: "Desinatör",
  Images: "Görseller", Unit: "Birim", Technics: "Teknikler", Created: "Oluşturuldu", Updated: "Güncellendi",
  AcceptanceDate: "Kabul tarihi", CompletionDate: "Tamamlanma", IsSample: "Numune",
};
const fieldLabel = (l: OrderLog) =>
  (l.changeType === "Other" && l.fieldName && FIELD_LABEL[l.fieldName]) || FIELD_LABEL[l.changeType] || FIELD_LABEL[l.fieldName || ""] || l.fieldName || l.changeType;

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtQty = (n?: number) => (n ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const person = (u?: { firstName?: string; lastName?: string } | null) => (u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : "");

/** Termin durumu: gecikti / yaklaşıyor / tamamlandı */
const deadlineInfo = (deadline?: string, status?: OrderStatus) => {
  if (!deadline) return { tone: "", text: "termin girilmemiş" };
  if (status === OrderStatus.Tamamlandi) return { tone: "", text: "tamamlandı" };
  if (status === OrderStatus.IptalEdildi) return { tone: "", text: "iptal edildi" };
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const d = new Date(deadline); d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (isNaN(days)) return { tone: "", text: "" };
  if (days < 0) return { tone: "late", text: `${-days} gün gecikti` };
  if (days === 0) return { tone: "soon", text: "bugün" };
  return { tone: days <= 3 ? "soon" : "", text: `${days} gün kaldı` };
};

const groupLogsByChangeToken = (logs: OrderLog[]): GroupedChange[] => {
  const grouped: { [key: string]: GroupedChange } = {};
  logs.forEach((log) => {
    const token = log.changeToken || log.logId;
    if (!grouped[token]) grouped[token] = { changeToken: token, changedAt: log.changedAt, changedByUserName: log.changedByUserName, changes: [] };
    grouped[token].changes.push(log);
  });
  return Object.values(grouped).sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
};

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  img: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.8" /><path d="m21 17-5-5-7 7" /></svg>,
};

/**
 * Sipariş detayı penceresi (Siparişler, Fatura Yönetimi, Atölye Kanban). /orders/detail/:id sayfasının
 * bilgi düzenini izler: başlıkta model + firma + durum, figür satırı (miktar / termin / atölye / tutar),
 * ardından bilgi grupları; ikinci sekmede gruplu değişiklik geçmişi.
 */
const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ isOpen, orderId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [loaded, setOrder] = useState<Order | null>(null);
  const [groupedChanges, setGroupedChanges] = useState<GroupedChange[]>([]);
  const [activeTab, setActiveTab] = useState<"info" | "history">("info");
  const [lightbox, setLightbox] = useState<{ url: string; label: string } | null>(null);
  const [broken, setBroken] = useState<Record<string, boolean>>({});
  const [alertModal, setAlertModal] = useState<AlertState>({ isOpen: false, message: "", type: "info" });
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Açılışta yükle; sekme ve önizleme sıfırlanır (bileşen bazı sayfalarda hep bağlı kalıyor)
  useEffect(() => {
    if (!isOpen || !orderId) return;
    let cancelled = false;
    setActiveTab("info");
    setLightbox(null);
    setBroken({});
    setOrder(null);
    setGroupedChanges([]);
    setLoading(true);
    (async () => {
      try {
        const [orderData, logsData] = await Promise.all([
          orderService.getById(orderId),
          orderService.getOrderLogs(orderId).catch(() => [] as OrderLog[]),
        ]);
        if (cancelled) return;
        if (!orderData) {
          setAlertModal({ isOpen: true, message: "Sipariş bulunamadı!", type: "error" });
          return;
        }
        setOrder(orderData);
        setGroupedChanges(groupLogsByChangeToken(logsData as OrderLog[]));
      } catch (error) {
        if (cancelled) return;
        console.error("❌ Failed to load order:", error);
        setAlertModal({ isOpen: true, message: "Sipariş yüklenemedi!", type: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, orderId]);

  // Esc: önce görsel önizleme / uyarı kapanır, sonra pencere
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (lightbox) setLightbox(null);
      else if (alertModal.isOpen) setAlertModal((a) => ({ ...a, isOpen: false }));
      else onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen, lightbox, alertModal.isOpen, onClose]);

  // Odak pencereye geçer, kapanınca açan öğeye döner
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus({ preventScroll: true });
    return () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); };
  }, [isOpen]);

  const images = useMemo(() => {
    if (!loaded) return [];
    return [
      ...(loaded.model?.imageUrl ? [{ key: "model", url: getImageUrl(loaded.model.imageUrl), label: "Model görseli", isModel: true }] : []),
      ...(loaded.images ?? []).map((im) => ({ key: im.orderImageId, url: getImageUrl(im.imageUrl), label: im.description || "Sipariş görseli", isModel: false })),
    ];
  }, [loaded]);

  if (!isOpen) return null;

  // Hep bağlı kalan sayfalarda yeniden açılışın ilk karesinde önceki sipariş görünmesin
  const order = loaded && loaded.orderId === orderId ? loaded : null;
  const busy = loading || (!!loaded && loaded.orderId !== orderId);
  const st = STATUS[order?.status ?? -1];
  const cur = order ? order.priceCurrency || order.currency || "TRY" : "TRY";
  const dl = deadlineInfo(order?.deadline, order?.status);
  const technics = order?.orderTechnics ?? [];
  const changeCount = groupedChanges.reduce((n, g) => n + g.changes.length, 0);

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-modal xl odm" role="dialog" aria-modal="true" aria-labelledby="odm-title" tabIndex={-1} ref={dialogRef}>
        <div className="po-modal-head">
          <div className="odm-headtext">
            <span className="po-kicker">{order ? `Sipariş · kabul ${fmtDate(order.acceptanceDate)}` : "Sipariş"}</span>
            <h3 id="odm-title">
              <span className="odm-code">{order ? order.model?.modelCode || order.model?.modelName || "Model kodu yok" : "Sipariş detayı"}</span>
              {order && <span className={`po-badge ${st?.tone || "muted"}`}>{st?.label || "Bilinmiyor"}</span>}
              {order?.isSample && <span className="po-badge muted">Numune</span>}
              {order?.priority && <span className={`po-badge ${order.priority === "Acil" ? "err" : order.priority === "Yüksek" ? "warn" : "muted"}`}>Öncelik: {order.priority}</span>}
            </h3>
            {order && (
              <p className="odm-sub">
                <b>{order.firm?.firmName || "Firma belirtilmemiş"}</b>
                {order.firm?.firmCode ? ` · ${order.firm.firmCode}` : ""}
                {order.model?.modelName && order.model.modelName !== order.model.modelCode ? ` · ${order.model.modelName}` : ""}
              </p>
            )}
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          {busy ? (
            <div className="po-loading"><span className="po-spin" />Sipariş yükleniyor…</div>
          ) : !order ? (
            <div className="po-blank"><b>Sipariş bulunamadı</b>Silinmiş ya da erişilemiyor olabilir.</div>
          ) : (
            <>
              <div className="po-tabs odm-tabs" role="tablist" aria-label="Sipariş detayı">
                <button type="button" role="tab" aria-selected={activeTab === "info"} className={activeTab === "info" ? "on" : ""} onClick={() => setActiveTab("info")}>Bilgiler</button>
                <button type="button" role="tab" aria-selected={activeTab === "history"} className={activeTab === "history" ? "on" : ""} onClick={() => setActiveTab("history")}>
                  Geçmiş<span>{groupedChanges.length}</span>
                </button>
              </div>

              {activeTab === "info" ? (
                <div className="odm-info" role="tabpanel">
                  <div className="po-figs odm-figs">
                    <div className="po-fig">
                      <span className="l">Miktar</span>
                      <span className="v">{fmtQty(order.quantity)} <small>{UNIT_LABEL[order.unit ?? 1] || "Adet"}</small></span>
                      <span className="s">{order.pieceCount && order.pieceCount > 1 ? `${order.pieceCount} parça / takım` : order.width || order.height ? `${order.width ?? "—"} × ${order.height ?? "—"}` : " "}</span>
                    </div>
                    <div className={`po-fig ${dl.tone}`}>
                      <span className="l">Termin</span>
                      <span className="v">{fmtDate(order.deadline)}</span>
                      <span className="s">{dl.text || " "}</span>
                    </div>
                    <div className="po-fig">
                      <span className="l">Atölye</span>
                      <span className={`v ${order.workshop ? "" : "odm-muted"}`} title={order.workshop?.name}>{order.workshop?.name || "Atanmadı"}</span>
                      <span className="s">{person(order.operator) || "sorumlu atanmamış"}</span>
                    </div>
                    <div className="po-fig">
                      <span className="l">Tutar</span>
                      <span className={`v ${order.price ? "" : "odm-muted"}`}>{order.price ? formatCurrency(order.price * order.quantity, cur) : "—"}</span>
                      <span className="s">{order.price ? `birim ${formatCurrency(order.price, cur)}` : "fiyat girilmemiş"}</span>
                    </div>
                  </div>

                  <section className="po-sec">
                    <h3>Sipariş bilgileri</h3>
                    <dl className="po-facts odm-facts">
                      <div><dt>Firma</dt><dd>{order.firm?.firmName || "—"}{order.firm?.firmCode && <small>{order.firm.firmCode}</small>}</dd></div>
                      <div><dt>Model</dt><dd>{order.model?.modelCode || order.model?.modelName || "—"}{order.model?.modelName && order.model.modelName !== order.model.modelCode && <small>{order.model.modelName}</small>}</dd></div>
                      <div><dt>Birim fiyat</dt><dd className={order.price ? "" : "odm-muted"}>{order.price ? formatCurrency(order.price, cur) : "—"}</dd></div>
                      <div><dt>Öncelik</dt><dd className={order.priority ? "" : "odm-muted"}>{order.priority || "Normal"}</dd></div>
                      {(order.invoice || order.invoiceNumber) && <div><dt>Fatura</dt><dd>{[order.invoice, order.invoiceNumber].filter(Boolean).join(" · ")}</dd></div>}
                    </dl>
                  </section>

                  <section className="po-sec">
                    <h3>Atölye ve ekip</h3>
                    <dl className="po-facts odm-facts">
                      <div><dt>Atölye</dt><dd className={order.workshop ? "" : "odm-muted"}>{order.workshop?.name || "Atanmadı"}</dd></div>
                      <div><dt>Sorumlu</dt><dd className={order.operator ? "" : "odm-muted"}>{person(order.operator) || "Atanmadı"}</dd></div>
                      <div><dt>Desinatör</dt><dd className={order.modelistUser ? "" : "odm-muted"}>{person(order.modelistUser) || "Atanmadı"}</dd></div>
                      {order.firm?.contactPerson ? (
                        <div><dt>Firma yetkilisi</dt><dd>{order.firm.contactPerson}{order.firm.phone && <small><a href={`tel:${order.firm.phone.replace(/\s/g, "")}`}>{order.firm.phone}</a></small>}</dd></div>
                      ) : null}
                    </dl>
                  </section>

                  <section className="po-sec">
                    <h3>Tarihler</h3>
                    <dl className="po-facts odm-facts">
                      <div><dt>Kabul</dt><dd>{fmtDateTime(order.acceptanceDate)}</dd></div>
                      <div><dt>Termin</dt><dd className={dl.tone === "late" ? "odm-late" : ""}>{fmtDate(order.deadline)}</dd></div>
                      <div><dt>Tamamlanma</dt><dd className={order.completionDate ? "" : "odm-muted"}>{order.completionDate ? fmtDateTime(order.completionDate) : "—"}</dd></div>
                      <div><dt>Oluşturma</dt><dd>{fmtDateTime(order.createdAt)}{order.createdBy && <small>{order.createdBy}</small>}</dd></div>
                    </dl>
                  </section>

                  <div className="odm-pair">
                    <section className="po-sec">
                      <h3>Teknikler{technics.length > 0 && <em>{technics.length}</em>}</h3>
                      {technics.length > 0 ? (
                        <div className="odm-tags">{technics.map((ot, i) => <span key={ot.orderTechnicId || i} className="odm-tag">{ot.technic?.name || "—"}</span>)}</div>
                      ) : <p className="odm-empty">Teknik seçilmemiş.</p>}
                    </section>
                    <section className="po-sec">
                      <h3>Not</h3>
                      {order.note ? <p className="odm-note">{order.note}</p> : <p className="odm-empty">Not eklenmemiş.</p>}
                    </section>
                  </div>

                  {images.length > 0 && (
                    <section className="po-sec">
                      <h3>Görseller<em>{images.length}</em></h3>
                      <div className="odm-images">
                        {images.map((im) => (
                          <button key={im.key} type="button" className={`odm-img ${im.isModel ? "model" : ""} ${broken[im.key] ? "broken" : ""}`}
                            onClick={() => !broken[im.key] && setLightbox({ url: im.url, label: im.label })} aria-label={`${im.label} — büyüt`} disabled={!!broken[im.key]}>
                            {broken[im.key] ? (
                              <span className="odm-img-ph">{I.img}Görsel yüklenemedi</span>
                            ) : (
                              <img src={im.url} alt={im.label} loading="lazy" onError={() => setBroken((b) => ({ ...b, [im.key]: true }))} />
                            )}
                            <span className="odm-img-cap">{im.label}</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              ) : (
                <div className="odm-history" role="tabpanel">
                  {groupedChanges.length === 0 ? (
                    <div className="po-blank small"><b>Henüz değişiklik kaydı yok</b>Sipariş düzenlendikçe değişiklikler burada listelenir.</div>
                  ) : (
                    <div className="po-timeline">
                      {groupedChanges.map((g) => (
                        <div key={g.changeToken} className="po-tl">
                          <b>{g.changedByUserName || "—"}</b>
                          <small>{fmtDateTime(g.changedAt)}{g.changes.length > 1 ? ` · ${g.changes.length} değişiklik` : ""}</small>
                          <ul className="po-tl-lines">
                            {g.changes.map((c, idx) => (
                              <li key={c.logId || idx} className="odm-ch">
                                <span className="odm-ch-field">{fieldLabel(c)}</span>
                                <span className="odm-ch-val">
                                  {c.oldValue || c.newValue ? (
                                    <>
                                      {c.oldValue && <span className="odm-old">{c.oldValue}</span>}
                                      {c.oldValue && c.newValue && <span className="odm-arrow" aria-label="yeni değer">→</span>}
                                      {c.newValue && <span className="odm-new">{c.newValue}</span>}
                                    </>
                                  ) : (
                                    <span className="odm-desc">{c.changeDescription || c.description || "—"}</span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint">
            {order ? (
              order.updatedAt ? <>Son güncelleme <b>{fmtDateTime(order.updatedAt)}</b>{order.updatedBy ? ` · ${order.updatedBy}` : ""}</>
                : <><b>{changeCount}</b> değişiklik kaydı</>
            ) : " "}
          </span>
          <button type="button" className="po-btn" onClick={onClose}>Kapat</button>
        </div>
      </div>

      {lightbox && (
        <div className="odm-lightbox" role="dialog" aria-modal="true" aria-label={lightbox.label} onMouseDown={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}>
          <button type="button" className="odm-lightbox-x" aria-label="Önizlemeyi kapat" onClick={() => setLightbox(null)}>{I.close}</button>
          <img src={lightbox.url} alt={lightbox.label} />
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

export default OrderDetailModal;
